import ClearIcon from '@mui/icons-material/Clear';
import { Box, Chip } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { FC, HTMLAttributes, useCallback } from 'react';
import { MultiSelectIdsType, SelectOption } from 'basics/options/options.types';
import ArrowDownIcon from 'components/Icons/ArrowDownIcon';
import FormHelperText from 'yoda-ui/components/Form/FormHelperText';
import { TagStyle } from 'yoda-ui/components/Form/SelectYoda/MultiSelectYoda/MultiSelectYoda.styles';
import { MultiSelectYodaInputProps } from 'yoda-ui/components/Form/SelectYoda/MultiSelectYoda/MultiSelectYoda.types';
import { selectStyle, textFieldStyle } from 'yoda-ui/components/Form/SelectYoda/SelectYoda.styles';
import TextField from 'yoda-ui/components/Form/TextField';
import Paper from 'yoda-ui/components/Paper';
import { findMultipleOptionsByIds } from 'yoda-ui/utils/options.utils';
import { useYodaField } from 'yoda-ui/yodaForm';
import { YodaSpacing } from 'yoda-ui/yodaTheme';

const MultiSelectYoda: FC<MultiSelectYodaInputProps> = ({
  defaultValue,
  disableClearable,
  disabled,
  freeOption = false,
  label,
  name,
  onChange,
  options,
  placeholder,
  required,
  validation,
  clearOnBlur = false,
  enableSelectAll = false,
  disableRenderTags = false,
  onSelectedChange,
  onDeleteOption,
  controlledValue,
}) => {
  const {
    onChangeFieldSynthetic,
    fieldValue,
    fieldDefaultValue,
    fieldShowError,
    fieldErrorMessage,
  } = useYodaField({ name, defaultValue, onChange, validation });

  // eslint-disable-next-line max-len
  const findCurrentMultiOptionsByIds = useCallback((idsList: MultiSelectIdsType) => findMultipleOptionsByIds(freeOption, options, idsList), [freeOption, options]);

  const sortedOptions = [...options].sort((a, b) => a.label.localeCompare(b.label));

  const allOption: SelectOption = { id: '__select_all__', label: 'Select All' };
  const finalOptions = enableSelectAll ? [allOption, ...sortedOptions] : sortedOptions;

  const currentValue = controlledValue ?? findCurrentMultiOptionsByIds(fieldValue);

  const handleChange: typeof onChangeFieldSynthetic = (event, newValue, reason) => {
    const values = newValue as SelectOption[];
    if (enableSelectAll && values.some((v) => v.id === '__select_all__')) {
      const allSelected = currentValue.length === sortedOptions.length;
      const next = allSelected ? [] : sortedOptions;
      onSelectedChange?.(next);
      onChangeFieldSynthetic(event, next, reason);
      return;
    }
    onSelectedChange?.(values);
    onChangeFieldSynthetic(event, newValue, reason);
  };

  return (
    <Box>
      <Autocomplete
        data-testid="multi-select"
        multiple
        popupIcon={ <ArrowDownIcon /> }
        PaperComponent={
          (props: HTMLAttributes<HTMLElement>) => (
            <Paper margin={ YodaSpacing.small }>{ props.children as React.ReactNode }</Paper>
          )
        }
        sx={ selectStyle }
        clearOnBlur={ clearOnBlur }
        freeSolo={ freeOption }
        disabled={ disabled }
        options={ finalOptions }
        getOptionLabel={ (option) => option.label }
        isOptionEqualToValue={ (option, value) => option.id === value.id }
        defaultValue={ findCurrentMultiOptionsByIds(fieldDefaultValue) }
        disableClearable={ disableClearable }
        value={ currentValue }
        onChange={ handleChange }
        renderTags={
          disableRenderTags
            ? () => null
            : (value: readonly SelectOption[], getTagProps) => value.map((option: SelectOption, index: number) => (
              <Chip
                variant="filled"
                label={ option.label }
                { ...getTagProps({ index }) }
                key={ option.id }
                sx={ TagStyle }
                deleteIcon={ <ClearIcon /> }
                onDelete={
                  () => {
                    onDeleteOption?.(option);
                    const next = value.filter((_, i) => i !== index);
                    onSelectedChange?.(next as SelectOption[]);
                  }
                }
              />
            ))
        }
        renderOption={
          (optionProps, option) => (
            <li { ...optionProps } key={ option.id }>{ option.label }</li>
          )
        }
        renderInput={
          (params) => (
            <TextField
              { ...params }
              sx={ textFieldStyle }
              label={ label }
              placeholder={ placeholder }
              required={ required }
              error={ fieldShowError }
            />
          )
        }
      />
      { fieldShowError && <FormHelperText error>{ fieldErrorMessage }</FormHelperText> }
    </Box>
  );
};

export default MultiSelectYoda;
